// KitchenScreen.tsx
import { Box, Tab, Tabs, Typography, Grid, IconButton, Tooltip, MenuItem, Select } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import React, { useState, useEffect, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux-store/hooks';
import { getDayTimestamps } from '../../utils';
import { getBills, getTableList } from '../../services';
import { BillType, ITables, ORDER_TYPE, TAKEAWAY_STATUS } from '../../all-types';
import { TakeawayOrdersDisplay } from './take-away-orders-display';
import { FullMenuTableCard, TakeawayCard } from './components';
import { DineInOrdersDisplay } from './dineIn-orders-display';

type TabValue = 'all' | 'dineIn' | 'takeaway';

const KitchenTabsAdmin: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState<TabValue>('all');
  const [takeawayBills, setTakeawayBills] = useState<BillType[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState<number>(10); // Default 10 seconds
  const allTables = useAppSelector((state) => state.preloadData.tables);
  const dispatcher = useAppDispatch();
  const pendingTakeAwayBills = takeawayBills?.filter(t => t.takeAwayStatus !== TAKEAWAY_STATUS.DONE);
  const [checkedItems, setCheckedItems] = useState<{[itemId: string] : boolean}>({});

  const fetchData = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const timestamp = getDayTimestamps();
      const response = await getBills(timestamp.morning, timestamp.night);
      if (response.bills) {
        const ongoingTakeawayBills = response.bills.filter(
          (bill: BillType) =>
            bill?.orderType === ORDER_TYPE.TAKEAWAY
        );
        setTakeawayBills(ongoingTakeawayBills);
      }
      await dispatcher(getTableList());
    } catch (e) {
      console.error('Failed to fetch data:', e);
    } finally {
      setIsRefreshing(false);
    }
  }, [dispatcher]);

  // Manual refresh handler
  const handleRefresh = () => {
    fetchData();
  };

  // Auto-refresh setup
  useEffect(() => {
    fetchData(); // Initial fetch

    const intervalId = setInterval(() => {
      fetchData();
    }, refreshInterval * 1000);

    return () => clearInterval(intervalId); // Cleanup on unmount
  }, [fetchData, refreshInterval]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: TabValue) => {
    setSelectedTab(newValue);
  };

  const activeDineInTables = allTables.filter(
    (table) =>
      table.billSession &&
      table.billSession.items &&
      table.billSession.items.length > 0
  );

  return (
    <Box p={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto">
          <Tab label="All" value="all" sx={{ textTransform: 'capitalize', fontWeight: 'bold' }} />
          <Tab label="Dine-In" value="dineIn" sx={{ textTransform: 'capitalize', fontWeight: 'bold' }} />
          <Tab label="Take Away" value="takeaway" sx={{ textTransform: 'capitalize', fontWeight: 'bold' }} />
        </Tabs>

        <Box display="flex" alignItems="center">
          <Select
            value={refreshInterval}
            onChange={(e) => setRefreshInterval(Number(e.target.value))}
            size="small"
            sx={{ mr: 1, width: '80px' }}
          >
            <MenuItem value={5}>5 sec</MenuItem>
            <MenuItem value={10}>10 sec</MenuItem>
            <MenuItem value={30}>30 sec</MenuItem>
            <MenuItem value={60}>60 sec</MenuItem>
          </Select>

          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} disabled={isRefreshing}>
              <RefreshIcon sx={{ transform: isRefreshing ? 'rotate(360deg)' : 'rotate(0deg)', transition: 'transform 1s' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {selectedTab === 'all' && (
        <Grid container spacing={3}>
          {activeDineInTables.length === 0 && takeawayBills.length === 0 && (
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{
                mt: 4,
                color: 'color15.main',
                textAlign: 'center',
                width: '100%',
              }}
            >
              No orders.
            </Typography>
          )}

          {activeDineInTables.map((table) => (
            <FullMenuTableCard key={table._id} table={table} />
          ))}

          {pendingTakeAwayBills?.map((order) => (
            <TakeawayCard key={order?._id || order.billNo} order={order} viewOnly={true}/>
          ))}
        </Grid>
      )}

      {selectedTab === 'dineIn' && (
        <DineInOrdersDisplay allTables={allTables} checkedItems={checkedItems} setCheckedItems={setCheckedItems}/>
      )}

      {selectedTab === 'takeaway' && (
        <TakeawayOrdersDisplay
          takeawayBills={takeawayBills}
          setTakeawayBills={setTakeawayBills}
          refersh={handleRefresh}
        />
      )}
    </Box>
  );
};

export { KitchenTabsAdmin };